import { DiscountActionForm, DiscountActionFormStatus, SavedDiscountAction, SavedDiscountActionMap, EditableBandedDiscount, DiscountTarget } from "app/pages/DiscountEditor/modules/DiscountActions/state";
import { Action, ActionType, SaveDiscountAction } from "app/pages/DiscountEditor/actionTypes";
import { combineReducers } from "redux";
import { DiscountValueType, DiscountActionTargetTypeEnum, ProductType, DiscountActionTypeTarget, DiscountBandTypeEnum } from "app/shared/model";
import { ProductFilterType, ProductFilter, SelectionMap, changeProductSelection, setProductFilterType, setProductType, parseProductFilterDto } from "app/shared/model/ProductFilter";
import { Fee } from "app/shared/model/Fee";
import {
    formatSavedMonetaryDiscountForInput,
    formatSavedPercentDiscountForInput,
    monetaryDiscountStringToApiNumber,
    revalidateEditableDiscountBand,
} from "app/pages/DiscountEditor/components/EditDiscountActionDialog/discountBandFieldValidation";
import { resolveFeeSelectionKey } from "app/pages/DiscountEditor/modules/DiscountActions/feeCodes";


function clearProductSelectionFromFilter(filter: ProductFilter): ProductFilter {
    if (filter.type === ProductFilterType.IncludeSpecific || filter.type === ProductFilterType.ExcludeSpecific) {
        return { ...filter, productCodes: {} };
    }
    if (filter.type === ProductFilterType.Homestay || filter.type === ProductFilterType.Residence) {
        return { ...filter, productCodes: {} };
    }
    return filter;
}

const emptyForm : DiscountActionForm = {
    status: DiscountActionFormStatus.ReadOnly
}

function fees(state: Fee[]|undefined, action: Action) : Fee[] {
    if (state === undefined)
        state = [];

    switch(action.type) {
        case ActionType.PageLoaded:
            return action.data.fees;
        case ActionType.FeesLoaded:
            return action.fees;
        default:
            return state;
    }
}

function form(state: DiscountActionForm|undefined, action: Action) : DiscountActionForm {
    if (state === undefined)
        state = emptyForm;

    switch(action.type) {

        case ActionType.ShowAddDiscountActionWizard:
            return { status: DiscountActionFormStatus.AddNewDiscountAction };
        
        case ActionType.ShowDeleteDiscountActionConfirmation: 
            return { 
                status: DiscountActionFormStatus.DeleteConfirmation, 
                id: action.discountAction.id, 
                name: action.discountAction.longName
            }
        case ActionType.CancelAddOrUpdateDiscountAction:
        case ActionType.CancelShowDeleteDiscountActionConfirmation:
            return emptyForm;

        case ActionType.SaveDiscountAction:
        case ActionType.DeleteDiscountAction:
            return emptyForm;

        case ActionType.StartAddingDiscountAction:
            return {
                status: DiscountActionFormStatus.AddOrEditDiscountBands,
                discount: newEditDiscountAction(action.bandType, action.target, action.initialTarget)
            }

        case ActionType.StartEditingDiscountAction:
            const toEdit = action.discountAction;
            return {
                status: DiscountActionFormStatus.AddOrEditDiscountBands,
                discount: createEditDiscountAction(toEdit)
            };

        case ActionType.ChangeDiscountActionValueType:
            if (state.status !== DiscountActionFormStatus.AddOrEditDiscountBands)
                return state;
            if (state.discount.discountValueType === action.valueType)
                return state;
            return {
                status: DiscountActionFormStatus.AddOrEditDiscountBands,
                discount: { 
                    ... state.discount,
                    discountValueType: action.valueType,
                    bands: state.discount.bands.map(b => ({
                        ...b,
                        value: '',
                        valueMessage: 'This is a required field',
                        isValid: false
                    }))
                }
            };

        case ActionType.UpdateEditableDiscountAction:
            return {
                status: DiscountActionFormStatus.AddOrEditDiscountBands,
                discount: action.discountAction
            };

        default:
            if ((action as any).type === "DISCOUNT/RESET_APPLICABLE_PRODUCTS" &&
                state.status === DiscountActionFormStatus.AddOrEditDiscountBands &&
                state.discount.target.type === DiscountActionTargetTypeEnum.OtherProducts) {
                return {
                    ...state,
                    discount: {
                        ...state.discount,
                        target: {
                            ...state.discount.target,
                            products: clearProductSelectionFromFilter(state.discount.target.products)
                        }
                    }
                };
            }
            return state;
    }
}

function newEditDiscountAction(
    bandType: DiscountBandTypeEnum,
    target: DiscountActionTargetTypeEnum,
    initialTarget?: DiscountTarget
) : EditableBandedDiscount {
    return {
        id: '',
        isNew: true,
        showValidation: false,
        discountValueType: DiscountValueType.Percent,
        target: initialTarget && initialTarget.type === target
            ? initialTarget
            : newDiscountActionTarget(target),
        bandType: bandType,
        bands: [{
            from: '',
            to: '',
            value: '',
            fromMessage: 'This is a required field',
            toMessage: 'This is a required field',
            valueMessage: 'This is a required field',
            isValid: false
        }]
    }
}

function newDiscountActionTarget(type: DiscountActionTargetTypeEnum) : DiscountTarget {
    switch(type) {
        case DiscountActionTargetTypeEnum.ApplicableProducts:
            return {
                type: DiscountActionTargetTypeEnum.ApplicableProducts
            };

        case DiscountActionTargetTypeEnum.OtherProducts:
            return {
                type: DiscountActionTargetTypeEnum.OtherProducts,
                products: {
                    type: ProductFilterType.IncludeSpecific,
                    productType: ProductType.Course,
                    productCodes: {}
                }
            };

        case DiscountActionTargetTypeEnum.Fees:
            return {
                type: DiscountActionTargetTypeEnum.Fees,
                fees: {}
            };

        default:
            throw new Error('Unknown discount action target type: ' + type)
    }
}

function createEditDiscountAction(action: SavedDiscountAction) : EditableBandedDiscount {
    const bandType = action.bandType || DiscountBandTypeEnum.Discrete;
    return {
        id: action.id,
        isNew: action.isNew,
        showValidation: false,
        discountValueType: action.valueType,
        target: action.target,
        bandType: bandType,
        bands: action.bands.map(b => {
        const band = {
            from: b.minUnits.toString(),
            to: b.maxUnits ? b.maxUnits.toString() : '',
            value:
                action.valueType === DiscountValueType.Percent
                    ? formatSavedPercentDiscountForInput(b.value)
                    : action.valueType === DiscountValueType.AmountPerUnit ||
                        action.valueType === DiscountValueType.OverrideUnitPrice
                      ? formatSavedMonetaryDiscountForInput(b.value)
                      : b.value.toString(),
            isValid: true
        };
        return revalidateEditableDiscountBand(band, action.valueType, bandType);
    }),
        currencyCode: action.currencyCode
    }
}

const defaultSavedDiscountActionMap : SavedDiscountActionMap = { 
    nextId: 1,
    items: { }
};

function actions(state: SavedDiscountActionMap|undefined, action: Action) : SavedDiscountActionMap {
    if (state == undefined) {
        state = defaultSavedDiscountActionMap;
    }

    switch(action.type) {
        case ActionType.PageLoaded:
        {
            const allActions : SavedDiscountAction[] = action.discount.actions.map(a => {
                const actionTarget = DiscountActionTypeTarget.FromEnum(a.target);
                const bandType = a.bandType ?? DiscountBandTypeEnum.Discrete;
                const mapBands = (bands: { minUnits?: number; maxUnits?: number; value?: number }[]) =>
                    bands.map(b => ({
                        minUnits: b.minUnits || 1,
                        maxUnits: bandType === DiscountBandTypeEnum.Discrete ? undefined : b.maxUnits,
                        value: b.value || 0
                    }));
                switch(a.target) {
                    case DiscountActionTargetTypeEnum.Fees:
                        const feeCodes = a.feeCodes ? a.feeCodes : [];
                        const availableFees = action.data.fees || [];
                        const fees : SelectionMap = {}
                        feeCodes.forEach(code => {
                            fees[resolveFeeSelectionKey(code, availableFees)] = true;
                        });
                        const feeAction : SavedDiscountAction = {
                            id: a.id,
                            longName: actionTarget.LongName,
                            isNew: false,
                            valueType: a.valueType,
                            target: {
                                type: DiscountActionTargetTypeEnum.Fees,
                                fees: fees
                            },
                            bandType: bandType,
                            bands: mapBands(a.bands),
                            currencyCode: a.currencyCode
                        }
                        return feeAction;

                    case DiscountActionTargetTypeEnum.OtherProducts:
                        const productFilter : ProductFilter = a.productFilter 
                            ? parseProductFilterDto(a.productFilter)
                            : {type: ProductFilterType.AnyProduct};

                        const otherProductAction : SavedDiscountAction = {
                            id: a.id,
                            isNew: false,
                            longName: actionTarget.LongName,
                            valueType: a.valueType,
                            bandType: bandType,
                            target: {
                                type: DiscountActionTargetTypeEnum.OtherProducts,
                                products: productFilter
                            },
                            bands: mapBands(a.bands),
                            currencyCode: a.currencyCode
                        }
                        return otherProductAction;

                    case DiscountActionTargetTypeEnum.ApplicableProducts:
                    default:
                        const productAction : SavedDiscountAction = {
                            id: a.id,
                            isNew: false,
                            longName: actionTarget.LongName,
                            target: {
                                type: DiscountActionTargetTypeEnum.ApplicableProducts
                            },
                            valueType: a.valueType,
                            bandType: bandType,
                            bands: mapBands(a.bands),
                            currencyCode: a.currencyCode
                        }
                        return productAction;
                }
            })

            const newState : any = {};
            allActions.forEach(a => newState[a.id] = a);
            return {
                nextId: state.nextId,
                items: newState
            }
        }

        case ActionType.SaveDiscountAction:
            let discountAction = createSavedDiscount(action.discountAction);
            let nextId = state.nextId;
            if (!action.discountAction.id) {
                discountAction = {... discountAction, id: 'NEW.' + nextId.toString()};
                nextId += 1;
            }

            return {
                nextId: nextId,
                items: {
                    ... state.items,
                    [discountAction.id]: discountAction
                }
            };

        case ActionType.DeleteDiscountAction: 
            let newItems : { [id: string]: SavedDiscountAction } = {};
            Object.keys(state.items).forEach(k => {
                if (k !== action.id && state !== undefined) {
                   newItems = {
                       ...newItems, 
                       [k]: state.items[k]
                   }
                }
            }); 
            return {
                nextId: state.nextId,
                items: newItems
            }
            
        case ActionType.SaveSuccessful:
        {
            const oldItems = state.items;
            const newItems : {[id: string]: SavedDiscountAction} = {};
            Object.keys(state.items).forEach(oldKey => {
                const oldItem = oldItems[oldKey];
                const newId = action.result.idMap[oldKey] || oldKey;
                const newItem : SavedDiscountAction = {
                    ... oldItem,
                    id: newId,
                    isNew: false,
                    errorMessage: undefined
                };

                newItems[newId] = newItem;
            })

            return {
                nextId: state.nextId,
                items: newItems
            }
        }

        default:
            if ((action as any).type === "DISCOUNT/RESET_APPLICABLE_PRODUCTS") {
                const newItems: { [id: string]: SavedDiscountAction } = {};
                Object.keys(state.items).forEach(key => {
                    const item = state.items[key];
                    if (item.target.type === DiscountActionTargetTypeEnum.OtherProducts) {
                        newItems[key] = {
                            ...item,
                            target: {
                                ...item.target,
                                products: clearProductSelectionFromFilter(item.target.products)
                            }
                        };
                    } else {
                        newItems[key] = item;
                    }
                });
                return { ...state, items: newItems };
            }
            return state;
    }
}

function createSavedDiscount(actionToSave: EditableBandedDiscount) : SavedDiscountAction  {
    const isDiscrete = actionToSave.bandType === DiscountBandTypeEnum.Discrete;
    const newAction = {
        id: actionToSave.id,
        isNew: actionToSave.isNew,
        longName: DiscountActionTypeTarget.FromEnum(actionToSave.target.type).LongName,
        valueType: actionToSave.discountValueType,
        bandType: actionToSave.bandType,
        target: actionToSave.target,
        bands: actionToSave.bands.map(x => ({
            minUnits: parseInt(x.from, 10),
            maxUnits: isDiscrete ? undefined : (x.to ? parseInt(x.to, 10) : undefined),
            value: monetaryDiscountStringToApiNumber(actionToSave.discountValueType, x.value)
        })),
        currencyCode: actionToSave.currencyCode
    }

    newAction.bands.sort((a, b) => a.minUnits - b.minUnits);
    return newAction;
}

export const reducer = combineReducers({
    form,
    actions,
    fees
});